// components/MenuCategorySelector.jsx
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { onValue } from 'firebase/database';
import { dbRef } from 'utils/dbRef';
import Button from 'components/ui/Button';

/**
 * MenuCategorySelector Component
 *
 * Renders category buttons in the order specified in the database.
 * Falls back to natural order if no custom order is defined.
 */
const MenuCategorySelector = ({
  categories = [],
  activeCategory,
  handleCategoryChange,
}) => {
  const [categoryOrder, setCategoryOrder] = useState([]);
  const [loading, setLoading] = useState(true);

  // ============================================================================
  // Effects
  // ============================================================================

  // Subscribe to category order changes in Firebase
  useEffect(() => {
    if (!categories.length) {
      setLoading(false);
      return;
    }

    const categoryOrderRef = dbRef('siteInfo/categoryOrder');

    const unsubscribe = onValue(
      categoryOrderRef,
      (snapshot) => {
        const orderData = snapshot.val();

        if (orderData && Array.isArray(orderData)) {
          setCategoryOrder(orderData);
        } else {
          // Fallback: use natural order of categories
          setCategoryOrder(categories.map((cat) => cat.id));
        }

        setLoading(false);
      },
      (error) => {
        // Fallback to natural order on error
        Sentry.captureException(error, {
          tags: {
            component: 'MenuCategorySelector',
            action: 'fetchCategoryOrder',
          },
        });
        setCategoryOrder(categories.map((cat) => cat.id));
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [categories]);

  // ============================================================================
  // Computed Values
  // ============================================================================

  // Sort categories based on the database order
  const sortedCategories = useMemo(() => {
    if (!categoryOrder.length) return categories;

    const ordered = categoryOrder
      .map((categoryId) => categories.find((cat) => cat.id === categoryId))
      .filter(Boolean);

    // Add any new categories that aren't in the order array
    const newCategories = categories.filter(
      (cat) => !categoryOrder.includes(cat.id),
    );

    return [...ordered, ...newCategories];
  }, [categories, categoryOrder]);

  // ============================================================================
  // Render
  // ============================================================================

  // Don't render while loading or if no categories
  if (loading || !categories.length) {
    return null;
  }

  return (
    <>
      {sortedCategories.map((category) => (
        <Button
          key={category.id}
          variant={activeCategory === category.id ? 'primary' : 'secondary'}
          size="small"
          // Compact pill, but a 44px-tall target for thumbs (#245).
          style={{ minHeight: '4.4rem' }}
          defaultColor={activeCategory === category.id ? 'tertiary' : 'primary'}
          hoverColor={
            activeCategory === category.id ? 'tertiaryDark' : 'primaryDark'
          }
          onClick={() => handleCategoryChange(category.id)}
          role="tab"
          aria-selected={activeCategory === category.id}
          aria-controls={`panel-${category.id}`}
          id={`tab-${category.id}`}
        >
          {category.name}
        </Button>
      ))}
    </>
  );
};

MenuCategorySelector.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }),
  ),
  activeCategory: PropTypes.string,
  handleCategoryChange: PropTypes.func.isRequired,
};

export default MenuCategorySelector;
